import { getCsrfToken } from './auth';

/**
 * The one place that talks to the Risk Management API (`/api/risk/*`).
 *
 * Same contract as `reviewApi.js`: CSRF on every write, and FastAPI's `detail` surfaced as the
 * error message — these endpoints write it as a sentence a person can act on.
 */
async function call(method, url, body, { form } = {}) {
  // no-store: a risk read is always live — never answered from the browser's HTTP cache.
  const opts = { method, headers: {}, credentials: 'include', cache: 'no-store' };
  if (form) {
    opts.body = form;
  } else if (body !== undefined) {
    opts.headers['Content-Type'] = 'application/json';
    opts.body = JSON.stringify(body);
  }
  if (method !== 'GET') opts.headers['X-CSRF-Token'] = getCsrfToken();

  const res = await fetch(url, opts);
  let data = null;
  try { data = await res.json(); } catch (_) { /* a 500 can arrive as HTML */ }
  if (!res.ok) {
    const detail = data?.detail;
    const isObject = detail && typeof detail === 'object';
    const err = new Error((isObject ? detail.message : detail) || `Request failed (${res.status})`);
    err.status = res.status;
    if (isObject) err.detail = detail;   // structured detail, e.g. an unfit mitigation type
    throw err;
  }
  return data;
}

const q = (params) => {
  const s = new URLSearchParams();
  Object.entries(params || {}).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '' && v !== false) s.set(k, v);
  });
  const str = s.toString();
  return str ? `?${str}` : '';
};

// XHR, not fetch: fetch cannot report UPLOAD progress. `onProgress(0..1)` as bytes go out.
function sendFile(method, url, file, onProgress) {
  return new Promise((resolve, reject) => {
    const form = new FormData();
    form.append('file', file);
    const xhr = new XMLHttpRequest();
    xhr.open(method, url);
    xhr.withCredentials = true;
    xhr.setRequestHeader('X-CSRF-Token', getCsrfToken());
    xhr.upload.onprogress = (e) => { if (e.lengthComputable && onProgress) onProgress(e.loaded / e.total); };
    xhr.onload = () => {
      let data = null;
      try { data = JSON.parse(xhr.responseText); } catch (_) { /* a 500 can arrive as HTML */ }
      if (xhr.status >= 200 && xhr.status < 300) return resolve(data);
      const detail = data?.detail;
      const err = new Error((detail && typeof detail === 'object' ? detail.message : detail) || `Request failed (${xhr.status})`);
      err.status = xhr.status;
      return reject(err);
    };
    xhr.onerror = () => reject(new Error('The upload failed — check your connection and try again.'));
    xhr.send(form);
  });
}

export const riskApi = {
  call,
  q,
  // panels
  setup: () => call('GET', '/api/risk/setup'),
  filters: (projectId, type, typeLabel) =>
    call('GET', `/api/risk/filters${q({ project_id: projectId, type, type_label: typeLabel })}`),
  checkFilter: (body) => call('POST', '/api/risk/check-filter', body),
  panels: () => call('GET', '/api/risk/panels'),
  createPanel: (body) => call('POST', '/api/risk/panels', body),
  updatePanel: (id, body) => call('PUT', `/api/risk/panels/${id}`, body),
  reorderPanels: (ids) => call('POST', '/api/risk/panels/reorder', { ordered_ids: ids }),
  panelConfig: (id) => call('GET', `/api/risk/panels/${id}/config`),
  deletePanel: (id) => call('DELETE', `/api/config/${id}`),
  // Traceability panels whose level 1 is Risk, shown on the Risk home — a link, not a copy.
  tracePanels: () => call('GET', '/api/risk/trace-panels'),
  linkTracePanel: (id) => call('POST', '/api/risk/trace-panels', { setup_id: id }),
  unlinkTracePanel: (id) => call('DELETE', `/api/risk/trace-panels/${id}`),

  // views
  summary: (id, params) => call('GET', `/api/risk/panels/${id}/summary${q(params)}`),
  rows: (id, params) => call('GET', `/api/risk/panels/${id}/rows${q(params)}`),
  risk: (id, itemId, params) => call('GET', `/api/risk/panels/${id}/risks/${encodeURIComponent(itemId)}${q(params)}`),
  // NDJSON: `onLine({type: 'progress', done, total})` per link, then `{type: 'done', urls}`.
  riskLinks: async (id, itemId, onLine, signal) => {
    const res = await fetch(`/api/risk/panels/${id}/risks/${encodeURIComponent(itemId)}/links`,
      { credentials: 'include', signal, headers: { Accept: 'application/x-ndjson' } });
    if (!res.ok) throw new Error(`Request failed (${res.status})`);
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buf = '';
    const emit = (text) => { if (text.trim()) onLine(JSON.parse(text)); };
    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;
      buf += decoder.decode(value, { stream: true });
      let nl;
      while ((nl = buf.indexOf('\n')) >= 0) { emit(buf.slice(0, nl)); buf = buf.slice(nl + 1); }
    }
    emit(buf + decoder.decode());
  },
  riskForm: (id, itemId) => call('GET', `/api/risk/panels/${id}/risks/${encodeURIComponent(itemId)}/form`),
  // ＋ Add mitigation on the risk detail page — the matrix's add / ✨ Generate with AI flow.
  mitigationOptions: (id, itemId) => call('GET', `/api/risk/panels/${id}/risks/${encodeURIComponent(itemId)}/mitigation-options`),
  addMitigation: (id, itemId, body) => call('POST', `/api/risk/panels/${id}/risks/${encodeURIComponent(itemId)}/mitigations`, body),
  dupCheckMitigations: (id, itemId, typeCode, drafts) => call('POST', `/api/risk/panels/${id}/risks/${encodeURIComponent(itemId)}/mitigations/dup-check`, { type_code: typeCode, drafts }),
  draftMitigations: (id, itemId, typeCode) => call('POST', `/api/risk/panels/${id}/risks/${encodeURIComponent(itemId)}/mitigations/ai`, { type_code: typeCode }),
  refreshSettings: (id) => call('POST', `/api/risk/panels/${id}/settings/refresh`),

  // baselines & change (Phase 2)
  baselines: (id) => call('GET', `/api/risk/panels/${id}/baselines`),
  takeBaseline: (id, body) => call('POST', `/api/risk/panels/${id}/baselines`, body),
  archiveBaseline: (id, bid, archived = true) =>
    call('POST', `/api/risk/panels/${id}/baselines/${bid}/archive${q({ archived: archived ? 'true' : 'false' })}`),
  compare: (id, a, b) => call('GET', `/api/risk/panels/${id}/compare${q({ a, b })}`),
  analyse: (id, body) => call('POST', `/api/risk/panels/${id}/compare/analysis`, body),
  history: (id, itemId) => call('GET', `/api/risk/panels/${id}/risks/${encodeURIComponent(itemId)}/history`),
  trend: (id) => call('GET', `/api/risk/panels/${id}/trend`),
  relations: (id) => call('GET', `/api/risk/panels/${id}/relations`),
  runFilters: () => call('GET', '/api/risk/run-filters'),

  // import & review-before-create (Phase 3)
  upload: (id, file, onProgress) => sendFile('POST', `/api/risk/panels/${id}/imports`, file, onProgress),
  // A new copy of the file into the same import (step 2 → "Reload file").
  reloadFile: (bid, file, onProgress) => sendFile('PUT', `/api/risk/imports/${bid}/file`, file, onProgress),
  imports: (id) => call('GET', `/api/risk/panels/${id}/imports`),
  importState: (bid) => call('GET', `/api/risk/imports/${bid}`),
  deleteImport: (bid) => call('DELETE', `/api/risk/imports/${bid}`),
  setStructure: (bid, body) => call('PUT', `/api/risk/imports/${bid}/structure`, body),
  setMapping: (bid, body) => call('PUT', `/api/risk/imports/${bid}/mapping`, body),
  analyze: (bid, step = 'all') => call('POST', `/api/risk/imports/${bid}/analyze${q({ step })}`),
  stage: (bid) => call('POST', `/api/risk/imports/${bid}/stage`),
  stagingRows: (bid, params) => call('GET', `/api/risk/imports/${bid}/rows${q(params)}`),
  patchRow: (bid, rowId, body) => call('PATCH', `/api/risk/imports/${bid}/rows/${rowId}`, body),
  includeRows: (bid, body) => call('POST', `/api/risk/imports/${bid}/rows/include`, body),
  createAll: (bid, scope = 'selected') => call('POST', `/api/risk/imports/${bid}/create`, { scope }),
  recordBlob: async (bid) => {
    const res = await fetch(`/api/risk/imports/${bid}/record.xlsx`, { credentials: 'include' });
    if (!res.ok) throw new Error(`Download failed (${res.status})`);
    return res.blob();
  },

  // Risk Studio (Phase 4)
  studio: (id) => call('GET', `/api/risk/panels/${id}/studio`),
  studioTypes: (id) => call('GET', `/api/risk/panels/${id}/studio/types`),
  addSource: (id, body) => call('POST', `/api/risk/panels/${id}/studio/sources`, body),
  uploadSource: (id, file) => {
    const form = new FormData();
    form.append('file', file);
    return call('POST', `/api/risk/panels/${id}/studio/sources/upload`, undefined, { form });
  },
  patchSource: (sid, body) => call('PATCH', `/api/risk/studio/sources/${sid}`, body),
  deleteSource: (sid) => call('DELETE', `/api/risk/studio/sources/${sid}`),
  refreshSource: (sid) => call('POST', `/api/risk/studio/sources/${sid}/refresh`),
  fdaCodes: (term) => call('GET', `/api/risk/studio/fda-codes${q({ q: term })}`),
  run: (id, body) => call('POST', `/api/risk/panels/${id}/studio/runs`, body),
  getRun: (runId) => call('GET', `/api/risk/studio/runs/${runId}`),
  cancelRun: (runId) => call('POST', `/api/risk/studio/runs/${runId}/cancel`),
  deleteRun: (runId) => call('DELETE', `/api/risk/studio/runs/${runId}`),
  decide: (runId, rowId, state) => call('POST', `/api/risk/studio/runs/${runId}/rows/${rowId}`, { state }),
  traceIt: (runId, rowId) => call('POST', `/api/risk/studio/runs/${runId}/rows/${rowId}/trace`),
  reqTarget: (runId) => call('GET', `/api/risk/studio/runs/${runId}/req-target`),
  createMitigation: (runId, rowId, body) => call('POST', `/api/risk/studio/runs/${runId}/rows/${rowId}/create`, body),
  findDuplicates: (runId) => call('POST', `/api/risk/studio/runs/${runId}/duplicates`),
  cancelDuplicates: (runId) => call('POST', `/api/risk/studio/runs/${runId}/duplicates/cancel`),
  toReview: (id) => call('POST', `/api/risk/panels/${id}/studio/to-review`),
};
